/**
 * Shared authz-adjacent types.
 *
 * The backend and frontend both need the same vocabulary (subjects, actions,
 * membership shape, role shape) to agree on authorization decisions. This
 * module owns those shapes; everything else is domain-specific and lives in
 * the respective package.
 *
 * IMPORTANT: this module must stay runtime-free and dependency-light. It is
 * consumed by both a Node backend and a browser bundle; no Mongoose, no
 * React, no Apollo.
 */

// ── Vocabulary ─────────────────────────────────────────────────────────────

/**
 * Resource types that support role-based authz. Extend when adding new
 * first-class resources. Keep in lockstep with the backend Mongoose enum.
 */
/**
 * Role is NOT a group-scoped subject: the role catalogue is managed
 * globally by super-admins, so it doesn't need CASL rules with group-level
 * conditions. Keeping it off this list means the backend Mongoose enum
 * stays in lockstep.
 */
export const RESOURCE_TYPES = ['redirection', 'user', 'group'] as const;
export type ResourceType = (typeof RESOURCE_TYPES)[number];

/** Actions a role can permit for a given resource type. */
export const ROLE_ACTIONS = ['create', 'read', 'update', 'delete'] as const;
export type RoleAction = (typeof ROLE_ACTIONS)[number];

// ── Static names ───────────────────────────────────────────────────────────

/** The reserved bootstrap group whose group_admin members are super-admins. */
export const ROOT_GROUP_NAME = '__root__';

/** Canonical names for the seeded system roles. */
export const SYSTEM_ROLE_NAMES = {
  GROUP_ADMIN: 'group_admin',
  GROUP_EDITOR: 'group_editor',
  GROUP_VIEWER: 'group_viewer',
  REDIRECTION_ADMIN: 'redirection_admin',
  REDIRECTION_EDITOR: 'redirection_editor',
  REDIRECTION_VIEWER: 'redirection_viewer',
  USER_ADMIN: 'user_admin',
  USER_EDITOR: 'user_editor',
  USER_VIEWER: 'user_viewer',
} as const;

// ── Role hierarchy ────────────────────────────────────────────────────────
//
// The three system roles per resource type form a strict implication chain:
//
//   admin  ⇒  editor  ⇒  viewer
//
// i.e. an admin always implies the editor AND viewer roles for the same
// resource, and an editor always implies viewer. This is a product-level
// invariant: a user can't "edit without viewing" or "admin without editing".
//
// The mapping below lists, for each role, the roles it implies. The helper
// `completeRoleHierarchy(names)` takes a set of role names and returns the
// transitive closure, i.e. the same set augmented with every implied role.
//
// Non-system / custom roles (not present in the map) are passed through
// unchanged — they have no implications beyond themselves.

export const ROLE_IMPLIES: Readonly<Record<string, readonly string[]>> = {
  [SYSTEM_ROLE_NAMES.GROUP_ADMIN]: [
    SYSTEM_ROLE_NAMES.GROUP_EDITOR,
    SYSTEM_ROLE_NAMES.GROUP_VIEWER,
  ],
  [SYSTEM_ROLE_NAMES.GROUP_EDITOR]: [SYSTEM_ROLE_NAMES.GROUP_VIEWER],
  [SYSTEM_ROLE_NAMES.REDIRECTION_ADMIN]: [
    SYSTEM_ROLE_NAMES.REDIRECTION_EDITOR,
    SYSTEM_ROLE_NAMES.REDIRECTION_VIEWER,
  ],
  [SYSTEM_ROLE_NAMES.REDIRECTION_EDITOR]: [SYSTEM_ROLE_NAMES.REDIRECTION_VIEWER],
  [SYSTEM_ROLE_NAMES.USER_ADMIN]: [
    SYSTEM_ROLE_NAMES.USER_EDITOR,
    SYSTEM_ROLE_NAMES.USER_VIEWER,
  ],
  [SYSTEM_ROLE_NAMES.USER_EDITOR]: [SYSTEM_ROLE_NAMES.USER_VIEWER],
} as const;

/**
 * Return the transitive closure of a set of role NAMES under ROLE_IMPLIES.
 *
 *   completeRoleHierarchy(['user_admin'])
 *     → ['user_admin', 'user_editor', 'user_viewer']
 *
 *   completeRoleHierarchy(['redirection_editor', 'group_viewer'])
 *     → ['redirection_editor', 'redirection_viewer', 'group_viewer']
 *
 * Duplicates are removed; unknown names are preserved (custom roles with
 * no declared implications). Output order is stable but not sorted.
 */
export function completeRoleHierarchy(names: readonly string[]): string[] {
  const out = new Set<string>();
  for (const name of names) {
    if (out.has(name)) continue;
    out.add(name);
    const implied = ROLE_IMPLIES[name];
    if (implied) for (const sub of implied) out.add(sub);
  }
  return [...out];
}

// ── Plain-object shapes shared by both sides ───────────────────────────────
//
// These are the lightweight shapes that flow through the GraphQL API. The
// backend has richer Mongoose types; the frontend has richer display types.
// The authz layer only needs what's here.

export interface FlatRole {
  id: string;
  name: string;
  resourceType: ResourceType;
  actions: RoleAction[];
  isDisabled: boolean;
}

export interface FlatMembership {
  groupId: string;
  groupName: string;
  roles: FlatRole[];
  isDisabled: boolean;
}

/**
 * The minimum authenticated-user projection required to build an ability.
 * Backend's FlatUser and frontend's User both extend / conform to this.
 */
export interface FlatAuthUser {
  id: string;
  memberships: FlatMembership[];
}

// ── Resource subject shapes ────────────────────────────────────────────────
//
// CASL needs to map an instance back to its subject key. The backend and
// frontend share a `__typename → subject` mapping, so resources from either
// side resolve to the same CASL subject.

/** GraphQL typenames mapped to their CASL subject key. */
export const TYPENAME_TO_SUBJECT: Record<string, ResourceType> = {
  RedirectionConfig: 'redirection',
  User: 'user',
  Group: 'group',
};

/**
 * Minimal shape every group-scoped resource must expose so the ability can
 * evaluate `groupIds` conditions. Both backend Mongoose docs (lean) and
 * frontend GraphQL payloads satisfy this.
 */
export interface GroupScopedResource {
  groupIds?: Array<string | { toString(): string }>;
}
